/**
 * Cart empty state: tab switching and loading recently viewed products.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const TAB_YOUR_CART = 'your-cart';
const TAB_RECENTLY_VIEWED = 'recently-viewed';

/**
 * @param {HTMLElement} root
 */
function getRecentlyViewedSectionId(root) {
  const hidden = document.querySelector('[data-cart-recently-viewed-section]');
  if (hidden && hidden.dataset.sectionId) return hidden.dataset.sectionId;
  return root.dataset.recentlyViewedSectionId || 'cart-recently-viewed';
}

/**
 * @param {HTMLElement} root
 * @returns {Promise<string | null>}
 */
async function fetchRecentlyViewedMarkup(root) {
  const sectionId = getRecentlyViewedSectionId(root);
  const viewedProducts = RecentlyViewed.getProducts();
  if (viewedProducts.length === 0) return null;

  const url = new URL(typeof Theme !== 'undefined' && Theme.routes?.search_url
    ? Theme.routes.search_url
    : '/search', location.origin);
  url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');

  return sectionRenderer.getSectionHTML(sectionId, false, url);
}

/**
 * @param {HTMLElement} root
 * @param {string} html
 */
function injectRecentlyViewedContent(root, html) {
  const container = root.querySelector('[data-cart-empty-recently-viewed]');
  if (!container) return;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const content = parsed.getElementById('cart-empty-recently-viewed-content');
  if (content) {
    container.innerHTML = '';
    container.appendChild(content.cloneNode(true));
  }
  container.classList.add('loaded');
}

/**
 * @param {HTMLElement} root
 */
function showNoRecentlyViewed(root) {
  const container = root.querySelector('[data-cart-empty-recently-viewed]');
  if (!container) return;
  const message = container.dataset.emptyMessage || 'No recently viewed products';
  container.innerHTML = `<p class="cart-recently-viewed__empty">${message}</p>`;
  container.classList.add('loaded');
}

/**
 * @param {HTMLElement} root
 */
async function loadRecentlyViewed(root) {
  const container = root.querySelector('[data-cart-empty-recently-viewed]');
  if (!container || container.dataset.loaded === 'true') return;
  container.dataset.loaded = 'true';

  const viewedProducts = RecentlyViewed.getProducts();
  if (viewedProducts.length === 0) {
    showNoRecentlyViewed(root);
    return;
  }

  try {
    const html = await fetchRecentlyViewedMarkup(root);
    if (html) {
      injectRecentlyViewedContent(root, html);
    } else {
      showNoRecentlyViewed(root);
    }
  } catch {
    showNoRecentlyViewed(root);
  }
}

/**
 * @param {HTMLElement} root
 */
function setupTabs(root) {
  const tabs = root.querySelectorAll('.cart-empty-state__tab');
  const panels = root.querySelectorAll('.cart-empty-state__panel');

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabName = tab.dataset.tab;

      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.classList.toggle('cart-empty-state__tab--active', selected);
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === tabName;
        panel.classList.toggle('cart-empty-state__panel--hidden', !isActive);
        panel.hidden = !isActive;
        if (isActive && tabName === TAB_RECENTLY_VIEWED) {
          loadRecentlyViewed(root);
        }
      });
    });

    tab.addEventListener('keydown', (e) => {
      let target = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        target = tab.nextElementSibling;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        target = tab.previousElementSibling;
      }
      if (target && target.classList.contains('cart-empty-state__tab')) {
        target.focus();
        target.click();
      }
    });
  });
}

function init() {
  document.querySelectorAll('[data-cart-empty-state]').forEach((root) => {
    if (root.dataset.cartEmptyStateInit === 'true') return;
    root.dataset.cartEmptyStateInit = 'true';
    setupTabs(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
